import queryString from 'query-string';

import {
    CALENDAR_MIN_GRID_COLUMNS,
    CALENDAR_MIN_GRID_ROWS,
    CALENDAR_MOBILE_GRID_ROWS,
} from '@/constants/calendarWidget.js';
import { initialState } from '@/Helpers.js';
import Model from '@/models/Model.js';
import User from '@/models/User.js';
import WidgetTemplate from '@/models/WidgetTemplate.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

// Phone grid heights in dashboard rows. 4 rows fit a count tile; 10 rows (about 520px) fit the
// header, the filters and three list rows, with the rest scrolling inside the widget.
const MOBILE_TILE_HEIGHT = 4;
const MOBILE_LIST_HEIGHT = 10;

// Narrowest desktop width, in grid columns, at which each widget type's table stays readable.
// Narrower than this the widget shows its labelled count tile instead. Measured at a 1440px viewport.
// The calendar is not listed: it has no count tile and renders at least CALENDAR_MIN_GRID_COLUMNS wide.
const TABLE_MIN_WIDTHS = {
    announcements: 6,
    'due-dates': 4,
    flashcards: 4,
    meetings: 4,
    messages: 6,
    performance: 4,
    quizzes: 5,
    'recent-grades': 4,
};
const DEFAULT_TABLE_MIN_WIDTH = 6;

// Grid minimums for every widget except the calendar (StanbridgeDashboard's grid-item defaults).
const DEFAULT_MIN_LAYOUT_WIDTH = 2;
const DEFAULT_MIN_LAYOUT_HEIGHT = 4;

// Columns in the dashboard grid (StanbridgeDashboard's GRID_COLUMNS).
const DASHBOARD_GRID_COLUMNS = 12;

// Widgets whose list stays on a phone, at any saved desktop size. Everything else shrinks to a count tile.
const MOBILE_LIST_TYPES = [
    'due-dates',
    'announcements',
    'recent-grades',
    'calendar',
    'messages',
    'quizzes',
    'meetings',
];

export default class Widget extends Model {
    static entity = 'widgets';

    static modelName = 'Widget';

    static TABLE_MIN_WIDTHS = TABLE_MIN_WIDTHS;

    static fields() {
        return {
            id: this.attr(null),
            widget_template_id: this.attr(null),
            user_id: this.attr(null),
            name: this.attr(null),
            type: this.attr(null),
            description: this.attr(null),
            color: this.attr(null),
            settings: this.attr(null),
            layout_x: this.attr(null),
            layout_y: this.attr(null),
            layout_width: this.attr(null),
            layout_height: this.attr(null),
            mobile_position: this.attr(null),

            ...super.fields(),

            widget_template: this.belongsTo(WidgetTemplate, 'widget_template_id'),
            user: this.belongsTo(User, 'user_id'),

            is_preview: this.attr(false),
        };
    }

    /**
     * The grid item for this widget. A calendar saved below its minimum size (by the earlier calendar, or as
     * a new widget's default) renders at the minimum, shifted left if the wider span would leave the grid.
     *
     * @returns {{w: (number|*), x: (number|*), h: (number|*), name: *, y: (*|number), i}}
     */
    get layout() {
        const width = this.is_calendar ? Math.max(this.layout_width ?? 0, this.min_layout_width) : this.layout_width;
        const height = this.is_calendar
            ? Math.max(this.layout_height ?? 0, this.min_layout_height)
            : this.layout_height;
        const column = this.is_calendar ? Math.min(this.layout_x ?? 0, DASHBOARD_GRID_COLUMNS - width) : this.layout_x;

        /* eslint-disable id-denylist */
        return {
            x: column,
            y: this.layout_y,
            w: width,
            h: height,
            i: this.id,
            name: this.type_name,
        };
        /* eslint-enable id-denylist */
    }

    /**
     * @returns {*|null}
     */
    get background() {
        return this.color ? this.color : null;
    }

    /**
     * @returns {*}
     */
    get type_name() {
        return initialState('WidgetTypes')[this.type];
    }

    /**
     * @returns {string}
     */
    get signature() {
        return `${this.type}-${JSON.stringify(this.settings?.filters)}`;
    }

    /**
     * Returns an array of every cell that this widget occupies on the grid according to its coordinates and dimensions.
     * Each item in the array is an array of the column and row of the cell ([x, y]). Basically a cell coordinate.
     * @returns {*[]}
     */
    get occupied_grid_cells() {
        const cells = [];
        const { x, y, w, h } = this.layout;

        for (let column = x; column < x + w; column++) {
            for (let row = y; row < y + h; row++) {
                cells.push([column, row]);
            }
        }

        return cells;
    }

    /**
     * Initial / page size for widget list hydration.
     * Keep this large enough that medium widgets can scroll before the next page is required.
     *
     * @returns {number}
     */
    get per_page() {
        return 15;
    }

    /**
     * Widgets with a list keep it on a phone, so a student gets the same information as on desktop and
     * can open an item directly. Tile-only types (flashcards, performance) shrink to their count tile.
     * @returns {boolean}
     */
    get shows_list_on_mobile() {
        return MOBILE_LIST_TYPES.includes(this.type);
    }

    /**
     * The weekly Calendar widget, which has its own grid minimums and no count tile.
     * @returns {boolean}
     */
    get is_calendar() {
        return this.type === 'calendar';
    }

    /**
     * Narrowest grid span the widget can be resized to.
     * @returns {number}
     */
    get min_layout_width() {
        return this.is_calendar ? CALENDAR_MIN_GRID_COLUMNS : DEFAULT_MIN_LAYOUT_WIDTH;
    }

    /**
     * Shortest grid height the widget can be resized to.
     * @returns {number}
     */
    get min_layout_height() {
        return this.is_calendar ? CALENDAR_MIN_GRID_ROWS : DEFAULT_MIN_LAYOUT_HEIGHT;
    }

    /**
     * Grid rows the widget takes when the dashboard stacks widgets on a phone. The calendar needs
     * room for its day strip and a 24rem agenda.
     * @returns {number}
     */
    get mobile_height() {
        if (this.is_calendar) return CALENDAR_MOBILE_GRID_ROWS;

        return this.shows_list_on_mobile ? MOBILE_LIST_HEIGHT : MOBILE_TILE_HEIGHT;
    }

    /**
     * Narrowest width, in grid columns, at which this widget type still shows its table.
     * @returns {number}
     */
    get table_min_width() {
        return TABLE_MIN_WIDTHS[this.type] ?? DEFAULT_TABLE_MIN_WIDTH;
    }

    /**
     * On a phone only the list widgets stay a list. Elsewhere the width alone decides: narrower than the
     * widget's own table minimum is a count tile, the full grid width is 'large' and anything between is 'medium'.
     * The calendar is never a tile.
     *
     * @param isMobile
     * @returns {'small'|'medium'|'large'}
     */
    displayMode(isMobile) {
        if (isMobile) return this.shows_list_on_mobile ? 'medium' : 'small';

        // The calendar has no count tile; it falls back to its day view instead.
        if (this.is_calendar) return this.layout_width === DASHBOARD_GRID_COLUMNS ? 'large' : 'medium';

        if (this.layout_width < this.table_min_width) return 'small';

        return this.layout_width === DASHBOARD_GRID_COLUMNS ? 'large' : 'medium';
    }

    /**
     * @param params
     * @returns {Promise<{}|any>}
     */
    async hydrate(params = {}) {
        if (!this.id) return {};

        try {
            const { data } = await axios.get(
                `/api/widgets/${this.id}?preload=true&per_page=${this.per_page}&${queryString.stringify(params)}`,
                {
                    errorHandle: false,
                }
            );

            return data;
        } catch (_error) {
            throw new Error('There was an error retrieving data for this widget.');
        }
    }

    /**
     * @param filters
     * @returns {Promise<*>}
     */
    async filter(filters) {
        const settings = { ...this.settings, filters: { ...(this.settings.filters || {}), ...filters } };

        await this.$update(settings);

        try {
            const { response } = await Widget.api().put(
                `/api/widgets/${this.id}?reload=true&per_page=${this.per_page}`,
                { settings },
                { errorHandle: false }
            );

            return response.data;
        } catch (_error) {
            throw new Error('There was an error filtering this widget.');
        }
    }

    /**
     * @param layout
     * @returns {*&{layout_width: *, layout_height: *, layout_x: *, layout_y: *}}
     */
    layoutPayload(layout) {
        return {
            ...this.$toJson(),
            ...{
                layout_x: layout.x,
                layout_y: layout.y,
                layout_width: layout.w,
                layout_height: layout.h,
            },
        };
    }

    /**
     * @param filters
     * @param queryModifier
     * @returns {Promise<*>}
     */
    async changeFilters(filters, queryModifier) {
        const existingSettings = this.settings || {};

        const payload = {
            settings: {
                ...existingSettings,
                filters,
            },
        };

        await this.$update(payload);

        const query = new QueryBuilder(`/api/widgets/${this.id}`);

        if (queryModifier) {
            queryModifier(query);
        }

        const { response } = await Widget.api().put(query.toUrl(), payload);

        return response.data;
    }

    /**
     * @param queryModifier
     * @returns {Promise<*>}
     */
    async hydrateSidebar(queryModifier) {
        const query = new QueryBuilder(`/api/widgets/${this.id}`);

        if (queryModifier) {
            queryModifier(query);
        }

        const { response } = await Widget.api().get(query.toUrl());

        return response.data;
    }

    /**
     * @param name
     * @returns {*}
     */
    changeName(name) {
        const payload = this.$toJson();

        payload.name = name;

        return Widget.api().put(`/api/widgets/${this.id}`, payload);
    }

    /**
     * @returns {*}
     */
    async remove() {
        try {
            return await Widget.api().delete(`/api/widgets/${this.id}`);
        } catch (_error) {
            throw new Error('There was an error removing this widget.');
        }
    }
}
